import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from 'env';
import { MytaverseLogger } from '@helpers/logger';
import { MiniGameEvent, MiniGameId } from './types';
import { isActiveSince } from './readyClock';

export type MiniGameCloseReason =
  | 'user'
  | 'escape'
  | 'error'
  | 'entered'
  | 'switch'
  | 'unmount';

const RENEWS_ENTRY_WINDOW: ReadonlySet<MiniGameCloseReason> = new Set([
  'user',
  'escape',
  'error',
]);

interface IOpenMiniGame {
  id: MiniGameId;
  openedAt: number;
}

interface IUseMiniGamesOptions {
  eventId?: string;
  hasAvatar: boolean;
  done: boolean;
  failed: boolean;
  reconnecting: boolean;
  playStream: boolean;
}

export const miniGamesEnabled = () =>
  env.REACT_APP_FEATURE_LOADING_MINI_GAMES === 'true';

const withoutUrls = (message: string) =>
  message.replace(/\b[a-z][a-z0-9+.-]*:\/\/\S+/gi, '<url>');

const detailsOf = (event: MiniGameEvent) => {
  if (event.type === 'failed') {
    return { reason: event.reason, message: withoutUrls(event.message) };
  }

  if (event.type === 'finished') {
    return { score: event.score, best: event.best, newBest: event.newBest };
  }

  return {};
};

export const useMiniGames = ({
  eventId,
  hasAvatar,
  done,
  failed,
  reconnecting,
  playStream,
}: IUseMiniGamesOptions) => {
  const enabled = miniGamesEnabled();
  const [openId, setOpenId] = useState<MiniGameId | null>(null);
  const [switched, setSwitched] = useState(false);
  const [deadlineAt, setDeadlineAt] = useState<number | null>(null);
  const [entryWindow, setEntryWindow] = useState(0);
  const openRef = useRef<IOpenMiniGame | null>(null);
  const activityRef = useRef(0);
  const contextRef = useRef({ eventId, hasAvatar });

  useEffect(() => {
    contextRef.current = { eventId, hasAvatar };
  });

  const close = useCallback((reason: MiniGameCloseReason) => {
    const current = openRef.current;

    if (!current) {
      return;
    }

    openRef.current = null;
    MytaverseLogger.log('Mini game closed', {
      ...contextRef.current,
      game: current.id,
      reason,
      durationMs: Date.now() - current.openedAt,
    });
    setOpenId(null);

    if (RENEWS_ENTRY_WINDOW.has(reason)) {
      setEntryWindow((count) => count + 1);
    }
  }, []);

  const open = useCallback(
    (id: MiniGameId) => {
      if (openRef.current?.id === id) {
        return;
      }

      setSwitched(openRef.current !== null);
      close('switch');
      const openedAt = Date.now();
      openRef.current = { id, openedAt };
      activityRef.current = openedAt;
      MytaverseLogger.log('Mini game opened', {
        ...contextRef.current,
        game: id,
      });
      setOpenId(id);
    },
    [close],
  );

  const report = useCallback((event: MiniGameEvent) => {
    const current = openRef.current;

    if (!current || event.type === 'progress' || event.type === 'score') {
      return;
    }

    MytaverseLogger.log(`Mini game ${event.type}`, {
      ...contextRef.current,
      game: current.id,
      durationMs: Date.now() - current.openedAt,
      ...detailsOf(event),
    });
  }, []);

  const holdSeat = useCallback(() => {
    const current = openRef.current;

    if (!current || !isActiveSince(activityRef.current, Date.now())) {
      return false;
    }

    MytaverseLogger.log('Mini game held the seat', {
      ...contextRef.current,
      game: current.id,
      durationMs: Date.now() - current.openedAt,
    });
    setEntryWindow((count) => count + 1);

    return true;
  }, []);

  const armEntryDeadline = useCallback(
    (at: number | null) => {
      if (enabled) {
        setDeadlineAt(at);
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (failed) {
      close('error');
    }
  }, [failed, close]);

  useEffect(() => {
    if (playStream) {
      close('entered');
    }
  }, [playStream, close]);

  useEffect(() => () => close('unmount'), [close]);

  return {
    openId,
    switched,
    dockVisible: enabled && !failed && !reconnecting && !done,
    deadlineAt,
    entryWindow,
    activityRef,
    open,
    close,
    report,
    holdSeat,
    armEntryDeadline,
  };
};
